import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Row, Col, Button, Badge, ModalBody, Spinner } from "reactstrap";
import Head from "../../../layout/head/Head";
import { Block, Icon } from "../../../components/Component";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import {
  Table,
  Collapse as AntCollapse,
  Button as AntButton,
  TableColumnsType,
  Descriptions,
  List,
  Modal,
  Timeline,
  Popover,
  Empty,
} from "antd";
import FormatDate from "../../masters/DateFormater";
import supplierApis from "../../../api/master/supplier";
import costCenterApis from "../../../api/master/costCenter";
import sites from "../../../api/master/sites";
import GeneralLedgerApis from "../../../api/master/generalLedger";
import departments from "../../../api/master/departments";
import deliveryAddressApis from "../../../api/master/deliveryAddress";
import { PaperClipOutlined, DownloadOutlined } from "@ant-design/icons";
import Swal from "sweetalert2";
import purchaseOrderCreation from "../../../api/purchase-orders/pruchaseOrderCreation";
import moment from "moment";
import { nanoid } from "nanoid";
import { useNavigate } from "react-router";
import rolesApi from "../../../api/master/roles";
import dayjs from "dayjs";
import usersApis from "../../../api/master/users";
import purchaseRequestApis from "../../../api/master/purchaseRequest";
import { set } from "date-fns";
import misc from "../../../api/misc/dropDowns";
import { groupMaterialsBySiteStructure } from "../../../utils/Utils";

interface SelectOptions {
  label: string;
  value: string;
}

const { Panel } = AntCollapse;
const PurchaseRequest_Preview: React.FC = (props: any) => {
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [disableDraft, setDisableDraft] = useState(false);
  const navigate = useNavigate();
  const { watch, register } = useFormContext();
  const [sm, setSm] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const { control, handleSubmit } = useForm();
  const [sitesOptions, setSitesOptions] = useState<SelectOptions[]>([]);
  const [usersOptions, setUsersOptions] = useState<SelectOptions[]>([]);
  const [deliveryAddressList, setDeliveryAddressList] = useState<any>([]);
  const previousFormData = watch();
  const [deliveryAddressOptions, setDeliveryAddressOptions] = useState<
    SelectOptions[]
  >([]);
  const [roles, setRoles] = useState<any>([]);
  const [submitData, setSubmitData] = useState<any>("SUBMIT");
  const [tab, setTab] = useState("1");
  const WORK_LEVEL_TO_SEGMENT: Record<string, string> = {
    TOWER_BLOCK: "Tower",
    VILLA: "Villa",
    FLOOR: "Floor",
    FLAT: "Flat",
  };
  const previewHierarchyVis = useMemo(() => {
    const items = previousFormData?.items ?? [];
    if (!items.length) {
      return {
        segmentLevel: false,
        tower: false,
        floor: false,
        flat: false,
        location: false,
      };
    }
    return {
      segmentLevel: items.some(
        (item: any) =>
          item?.segmentLevel &&
          item.segmentLevel.toString().toLowerCase() !== "null"
      ),
      tower: items.some(
        (item: any) =>
          item?.towerBlockVillaId ||
          item?.towerBlockVillaLabel ||
          item?.towerBlockVillaName ||
          item?.siteStructureUnitId ||
          item?.unitName
      ),
      floor: items.some(
        (item: any) =>
          (item?.floorLabel != null && item?.floorLabel !== "") ||
          (item?.floorNo != null && item?.floorNo !== "")
      ),
      flat: items.some(
        (item: any) =>
          item?.flatLabel ||
          item?.flatName ||
          item?.siteStructureSubUnitId ||
          item?.subUnitName
      ),
      location: items.some(
        (item: any) => item?.locationLabel || item?.locationName
      ),
    };
  }, [previousFormData?.items]);

  const columns = useMemo(() => {
    const baseColumns: TableColumnsType<any> = [
      {
        title: "Material Category",
        dataIndex: "categoryLabel",
        key: "categoryLabel",
        width: 200,
        fixed: "left",
      },
      {
        title: "Material Name",
        dataIndex: "materialLabel",
        key: "materialLabel",
        width: 200,
        fixed: "left",
      },
      {
        title: "Specification",
        dataIndex: "specification",
        key: "specification",
        width: 200,
      },
      {
        title: "Req Qty",
        dataIndex: "quantity",
        key: "quantity",
        width: 150,
        render: (text, record) => `${text} ${record?.uomLabel}`,
      },
      {
        title: "Remarks",
        dataIndex: "remarks",
        key: "remarks",
        width: 150,
      },
    ];

    if (previewHierarchyVis.tower) {
      baseColumns.push({
        title:
          previousFormData?.segmentLevel === "Villa"
            ? "Villa"
            : "Tower / Block",
        dataIndex: "towerBlockVillaLabel",
        key: "towerBlockVillaLabel",
        width: 160,
        render: (_: any, record: any) =>
          record?.towerBlockVillaLabel ||
          record?.towerBlockVillaName ||
          record?.towerBlockName ||
          record?.unitName ||
          "-",
      });
    }

    if (previewHierarchyVis.floor) {
      baseColumns.push({
        title: "Floor",
        dataIndex: "floorLabel",
        key: "floorLabel",
        width: 120,
        render: (_: any, record: any) => record?.floorLabel || record?.floorNo || "-",
      });
    }

    if (previewHierarchyVis.flat) {
      baseColumns.push({
        title: "Flat",
        dataIndex: "flatLabel",
        key: "flatLabel",
        width: 140,
        render: (_: any, record: any) =>
          record?.flatLabel || record?.flatName || record?.subUnitName || "-",
      });
    }

    if (previewHierarchyVis.location) {
      baseColumns.push({
        title: "Location",
        dataIndex: "locationLabel",
        key: "locationLabel",
        width: 160,
        render: (_: any, record: any) =>
          record?.locationLabel || record?.locationName || "-",
      });
    }

    return baseColumns;
  }, [previewHierarchyVis]);

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  const fetchSites = async () => {
    try {
      const data = await usersApis.getCurrentUser();
      if (data) {
        let sitesOptions = data?.siteAccessibility?.map((item: any) => ({
          label: item.siteName,
          value: item.siteID.toString(),
        }));
        setSitesOptions(sitesOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (previousFormData.siteId) {
      fetchUsers(previousFormData.siteId);
    }
  }, []);

  const fetchUsers = async (siteId: any) => {
    try {
      const data = await misc.fetchSiteUsers(siteId);
      if (data) {
        console.log("Data", data);
        let usersOptions = data?.data?.map((item: any) => ({
          label: item.userName,
          value: item.userId.toString(),
        }));
        setUsersOptions(usersOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchDeliveryAddress = async () => {
    try {
      const { data, status } = await deliveryAddressApis.fetchDeliveryAddress();
      if (status) {
        let deliveryAddressOptions = data.map((item: any) => ({
          label: item.deliveryLocationName,
          value: item.id.toString(),
        }));
        setDeliveryAddressOptions(deliveryAddressOptions);
        setDeliveryAddressList(data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchApprovalHierarchyUsersByModuleId = async (moduleID: number, siteId: number) => {
    try {
      const res = await rolesApi.fetchHierarchyUsersByModuleId(
        moduleID,
        siteId
      );
      if (!res) {
        return;
      }

      const { data, status } = res;

      if (status) {
        setRoles(data || []);
      } else {
        setRoles([]);
      }
    } catch (error) {
      console.error("Error fetching hierarchy users:", error);
      setRoles([]);
    }
  };
  const fetchModuleConfig = async () => {
    try {
      const res = await rolesApi.getModuleConfig();
      let moduleID: number | undefined;

      // Find the ProcureToPay category first
      const procureToPayCategory = res?.data?.find(
        (category: any) => category.category === "ProcureToPay"
      );

      if (procureToPayCategory && procureToPayCategory.modules) {
        // Find the Purchase Order module
        const purchaseOrderModule = procureToPayCategory.modules.find(
          (module: any) => module.moduleName === "Purchase Request"
        );

        if (purchaseOrderModule) {
          moduleID = purchaseOrderModule.moduleID;
        }
      }

      if (moduleID && previousFormData?.siteId) {
        await fetchApprovalHierarchyUsersByModuleId(moduleID, Number(previousFormData.siteId));
      } else {
        console.warn("Module ID or Site ID is missing", {
          moduleID,
          siteId: previousFormData?.siteId
        });
        setRoles([]);
      }
    } catch (err) {
      console.error("Error fetching module config:", err);
      setRoles([]);
    }
  };

  useEffect(() => {
    console.log("previousFormData", previousFormData);
    fetchModuleConfig();
    fetchDeliveryAddress();
    fetchSites();
  }, []);

  const ShowAddressPopOver = ({ id }: any) => {
    if (!id) {
      return <></>;
    }
    let address = deliveryAddressList?.find((item: any) => item?.id == id);
    if (!address) {
      return <></>;
    }
    return (
      <ul>
        <li>
          <strong style={{ color: "#2263b3" }}>
            {address?.deliveryLocationName}
          </strong>
        </li>
        <li>{address?.addressLine1}</li>
        <li>{address?.addressLine2}</li>
        <li>
          {address?.city}, {address?.stateName}, {address?.countryName} -{" "}
          {address?.pincode}
        </li>
      </ul>
    );
  };

  const ApprovalTimelineContent = () => {
    const approvalsData = JSON.parse(previousFormData?.approvalsData);
    const isStrictMode = previousFormData?.isStrictApproval || false;
    if (
      !approvalsData ||
      approvalsData?.data?.length === 0 ||
      previousFormData?.approvalRequired == "false"
    )
      return <Badge color="outline-danger">No</Badge>;
    return (
      <Popover
        content={
          <Timeline className="approval-timeline">
            {approvalsData?.data?.map((approval: any, index: number) => {
              const Role = roles.find(
                (role: any) => role?.roleID == approval?.roleId
              );
              return (
                <Timeline.Item key={index}>
                  {
                    Role?.employees?.find((e: any) => e?.id == approval?.userId)
                      ?.name
                  }
                  <div className="designation">{Role?.roleName}</div>
                </Timeline.Item>
              );
            })}
          </Timeline>
        }
        title={<span style={{ color: "#2263b3" }}>Approval Hierarchy</span>}
      >
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer"
        }}>
          <span
            style={{
              color: "#2263b3",
            }}
          >
            <Badge color="outline-success">Yes</Badge>
          </span>
          {isStrictMode && (
            <Badge
              color="warning"
              style={{
                fontSize: "11px",
                cursor: "pointer",
                marginLeft: "4px"
              }}
              title="Sequential approvals required, Approvals must follow the sequence"
            >
              Strict
            </Badge>
          )}
        </div>
      </Popover>
    );
  };

  const onSubmit = (status: string) => async (data: any) => {
    if (status == "Draft") {
      console.log("DRAFT");
      setDisableDraft(true);
    } else if (status == "SUBMIT") {
      console.log("SUBMIT");
      setDisableSubmit(true);
    }
    const formData: any = new FormData();
    let prData: any = {
      ...previousFormData,
    };
    console.log("prData", prData);
    const segmentLevel =
      prData.workLevelSetting && prData.workLevelSetting !== "NONE"
        ? WORK_LEVEL_TO_SEGMENT[prData.workLevelSetting] ?? null
        : null;
    const prNumberValue =
      prData.purchaseRequestId || prData.prNumber || prData.prSequence || null;
    const normalizedStatus =
      status?.toUpperCase() === "SUBMIT" ? "SUBMIT" : "DRAFT";
    const resolvedSiteId =
      prData.siteId != null && prData.siteId !== ""
        ? Number(prData.siteId)
        : null;
    const resolvedBlockId =
      prData.levelType === "Block" && prData.blockId
        ? Number(prData.blockId)
        : null;
    const resolvedContractorId =
      prData.contractorId != null && prData.contractorId !== ""
        ? Number(prData.contractorId)
        : null;
    // Always pass "Site" as levelType to API
    const normalizedLevelType = "Site";
    
    // Determine if auto-generated based on numberingMode or if value is empty
    const isAutoGenerated = prData.numberingMode === 'auto' || !prNumberValue;

    const prHeader: any = {
      prNumber: isAutoGenerated ? null : prNumberValue,
      prSequence: isAutoGenerated ? null : prNumberValue,
      referenceIndentNumber: prData.referenceIndentNumber || null,
      indentDate: prData.indentDate
        ? dayjs(prData.indentDate).toISOString()
        : null,
      levelType: normalizedLevelType,
      siteId: resolvedSiteId,
      blockId: resolvedBlockId,
      requestedBy:
        prData.requestedBy != null && prData.requestedBy !== ""
          ? Number(prData.requestedBy)
          : null,
      needByDate: prData.needByDate
        ? dayjs(prData.needByDate).toISOString()
        : null,
      deliveryAddressId:
        prData.deliveryAddressId != null && prData.deliveryAddressId !== ""
          ? Number(prData.deliveryAddressId)
          : null,
      status: normalizedStatus,
      segmentLevel: segmentLevel ?? null,
      isStrictApproval: prData.isStrictApproval || false,
    };
    if (resolvedContractorId) {
      prHeader.contractorId = resolvedContractorId;
    }
    prHeader.towerBlockVillaId =
      prData.towerBlockVillaId != null && prData.towerBlockVillaId !== ""
        ? Number(prData.towerBlockVillaId)
        : null;
    prHeader.floorNo =
      prData.floorNo != null && prData.floorNo !== "" ? prData.floorNo : null;
    prHeader.flatId =
      prData.flatId != null && prData.flatId !== ""
        ? Number(prData.flatId)
        : null;
    prHeader.locationId =
      prData.locationId != null && prData.locationId !== ""
        ? Number(prData.locationId)
        : null;

    // Group materials by materialId and create siteStructures array
    const groupedMaterials = groupMaterialsBySiteStructure(prData?.items || []);

    const prLines = groupedMaterials.map((group: any) => {
      return {
        categoryId: Number(group.materialCategory),
        materialId: Number(group.materialName),
        quantity: Number(group.totalQuantity), // Total quantity across all site allocations
        uomId: Number(group.uomId),
        remarks: group.remarks || null,
        makeInfo: group.makeInfo?.length > 0 ? group.makeInfo : [],

        // Site structure allocations array
        siteStructures: group.siteStructures.map((site: any) => ({
          siteStructureUnitId: site.unitId ? Number(site.unitId) : null,
          floorNo: site.floorNo != null && site.floorNo !== "" ? String(site.floorNo) : null,
          siteStructureSubUnitId: site.subUnitId ? Number(site.subUnitId) : null,
          locationId: site.locationId ? Number(site.locationId) : null,
          quantity: Number(site.quantity),
        })),
      };
    });
    const prApprovals = JSON.parse(prData?.approvalsData);

    if (prApprovals?.data?.length > 0 && prData.approvalRequired) {
      formData.append("prApprovals", JSON.stringify(prApprovals?.data));
    } else {
      formData.append("prApprovals", JSON.stringify([]));
    }

    formData.append("prLines", JSON.stringify(prLines));

    formData.append("prHeader", JSON.stringify(prHeader));
    postPR(formData);
  };

  const postPR = async (data: any) => {
    console.log("data", data);
    // return
    try {
      const api: any = await purchaseRequestApis.purchaseRequestCreation(data);
      const { status, message, response } = api;
      if (status) {
        Swal.fire("Created!", response.prSequence, "success");
        navigate(`/view-purchase-requests`);
      } else {
        Swal.fire("Failed!", message, "error");
      }
    } catch (error) {
      Swal.fire("Failed!", "Something went wrong", "error");
    }
    setDisableSubmit(false);
    setDisableDraft(false);
  };

  return (
    <>
      <Head title="New - Purchase Request " />
      <div className="content clearfix">
        <form>
          <AntCollapse defaultActiveKey={["1", "2", "3", "4", "5"]}>
            <Panel header="General Information" key="1">
              {/* <div className="tab-content mt-0"> */}
              {/* <div className={`tab-pane${tab === "1" && " active"}`}> */}
              <Col className="justify-start w-100  flex-wrap mt-3 p-3">
                <div className="w-30 border-end ">
                  <div className="data-col align-center pt-1 ">
                    <div className="data-label w-45" style={{ whiteSpace: "nowrap" }}>
                      Reference Indent Number
                    </div>
                    <div className="data-value w-55 ">
                      {previousFormData.referenceIndentNumber}
                    </div>
                  </div>
                  <div className="data-col align-center pt-1">
                    <div className="data-label w-45">Indent Date</div>
                    <div className="data-value w-55">
                      {dayjs(previousFormData?.indentDate).format(
                        "DD-MMM-YYYY"
                      ) || "--"}
                    </div>
                  </div>
                  <div className="data-col align-center pt-1 ">
                    <div className="data-label w-45">Site</div>
                    <div className="data-value w-55 ">
                      {
                        sitesOptions.find(
                          (item) => item.value == previousFormData.siteId
                        )?.label
                      }
                    </div>
                  </div>
                  <div className="data-col align-center pt-1 ">
                    <div className="data-label w-45">Site Hierarchy</div>
                    <div className="data-value w-55 ">
                      {previousFormData.segmentLevel || "Site"}
                    </div>
                  </div>
                </div>
                <div className="w-30 border-end paddingLeft10  ">
                  <div className="data-col align-center pt-1 ">
                    <div className="data-label w-30">Requested By</div>
                    <div className="data-value w-70 ">
                      {
                        usersOptions.find(
                          (item) => item.value == previousFormData.requestedBy
                        )?.label
                      }
                    </div>
                  </div>
                  <div className="data-col align-center pt-1">
                    <div className="data-label w-30">Need By Date</div>
                    <div className="data-value w-70">
                      {dayjs(previousFormData?.needByDate).format(
                        "DD-MMM-YYYY"
                      ) || "--"}
                    </div>
                  </div>
                  <div className="data-col align-center pt-1">
                    <div className="data-label w-30">Approvals</div>
                    <div className="data-value w-70">
                      <ApprovalTimelineContent />
                    </div>
                  </div>
                </div>
                <div className="w-30 paddingLeft10">
                  <div className=" pt-1">
                    <div className="data-label w-9 mb-1">Delivery Address</div>
                    <div className="data-value w-90">
                      <ShowAddressPopOver
                        id={previousFormData?.deliveryAddressId}
                      />
                    </div>
                  </div>
                </div>
              </Col>
              {/* </div> */}
              {/* </div> */}
            </Panel>
            <Panel header="Material Information" key="2">
              <div className="table-responsive poTable">
                <div
                  style={{
                    width: screenSize - 170 + "px",
                    margin: "15px auto",
                  }}
                >
                  {columns && (
                    <Table
                      className="customTable"
                      columns={columns}
                      dataSource={previousFormData?.items}
                      scroll={{ x: 1000 }}
                      bordered
                      size="small"
                      pagination={{
                        defaultPageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100', '200'],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} materials`,
                      }}
                    />
                  )}
                </div>
              </div>
            </Panel>
          </AntCollapse>
          <div className="actions clearfix">
            <ul>
              <li>
                {disableSubmit ? (
                  <Button color="primary" type="button">
                    <Spinner size="sm" color="light" />
                  </Button>
                ) : (
                  <Button
                    color="primary"
                    type="submit"
                    onClick={() => {
                      setSubmitData("SUBMIT"); // Set submitData to "SUBMITTED"
                      handleSubmit(onSubmit("SUBMIT"))(); // Trigger form submission
                    }}
                  >
                    Submit
                  </Button>
                )}
              </li>
              <li>
                <Button
                  color="primary"
                  onClick={() => {
                    props.prev();
                  }}
                  className="btn-dim btn btn-secondary"
                >
                  Previous
                </Button>
              </li>
              <li>
                {disableDraft ? (
                  <Button color="secondary" type="button">
                    <Spinner size="sm" color="light" />
                  </Button>
                ) : (
                  <Button
                    color="secondary"
                    type="button"
                    onClick={() => {
                      setSubmitData("DRAFT");
                      setDisableDraft(true);
                      handleSubmit(onSubmit("DRAFT"))(); // Trigger form submission
                    }}
                  >
                    Save Draft
                  </Button>
                )}
              </li>
            </ul>
          </div>
        </form>
      </div>
    </>
  );
};

export default PurchaseRequest_Preview;
